import React, { useState, useEffect } from "react";
import {
  X,
  CheckCircle2,
  ExternalLink,
  Image as ImageIcon,
  ShieldCheck,
  Edit3,
  Lock,
  ZoomIn,
  AlertTriangle,
  FileText,
  Eye,
  User,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  PaymentVerificationItem,
  approvePaymentVerification,
  rejectPaymentVerification,
} from "../../services/admin";

interface PaymentVerificationModalProps {
  verification: PaymentVerificationItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultAction?: "approve" | "reject" | null;
}

export const PaymentVerificationModal: React.FC<PaymentVerificationModalProps> = ({
  verification,
  isOpen,
  onClose,
  onSuccess,
  defaultAction = null,
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [verifiedAmount, setVerifiedAmount] = useState<number | string>(0);
  const [adminNotes, setAdminNotes] = useState<string>("Payment received with thanks");
  const [isAmountEditable, setIsAmountEditable] = useState<boolean>(false);

  // Modals state
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [imageLoadError, setImageLoadError] = useState<boolean>(false);

  useEffect(() => {
    if (verification) {
      const initialAmt =
        verification.verified_amount !== undefined &&
        verification.verified_amount !== null &&
        Number(verification.verified_amount) > 0
          ? verification.verified_amount
          : verification.amount_paid !== undefined &&
            verification.amount_paid !== null
          ? verification.amount_paid
          : 0;
      setVerifiedAmount(initialAmt);
      setAdminNotes("Payment received with thanks");
      setIsAmountEditable(false);
      setIsPreviewOpen(false);
      setShowRejectModal(defaultAction === "reject");
      setRejectionReason("");
      setImageLoadError(false);
    }
  }, [verification, isOpen, defaultAction]);

  if (!isOpen || !verification) return null;

  const isPdfFile = (url?: string) => {
    if (!url) return false;
    const cleanUrl = url.toLowerCase().split("?")[0];
    return (
      cleanUrl.endsWith(".pdf") ||
      url.toLowerCase().includes("application/pdf") ||
      url.toLowerCase().includes("/pdf/") ||
      imageLoadError
    );
  };

  const handleApprove = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const numAmount = Number(verifiedAmount);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error("Verified amount must be greater than zero");
      return;
    }

    setSubmitting(true);
    try {
      await approvePaymentVerification({
        id: verification.id,
        payload: {
          verified_amount: numAmount,
          admin_notes: adminNotes.trim(),
        },
      });
      toast.success("Payment approved successfully");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to approve payment verification:", error);
      toast.error("Failed to approve payment verification");
    } finally {
      setSubmitting(false);
    }
  };

  const processRejection = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setSubmitting(true);
    try {
      await rejectPaymentVerification({
        id: verification.id,
        payload: {
          reason: rejectionReason.trim(),
        },
      });
      setShowRejectModal(false);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to reject payment verification:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatNumberWithCommas = (val: string | number) => {
    if (val === "" || val === undefined || val === null) return "";
    const str = String(val);
    const parts = str.split(".");
    const rawInt = parts[0].replace(/\D/g, "");
    const formattedInt = rawInt
      ? Number(rawInt).toLocaleString("en-US")
      : parts.length > 1
      ? "0"
      : "";
    if (parts.length > 1) {
      const rawDec = parts[1].replace(/\D/g, "");
      return `${formattedInt}.${rawDec}`;
    }
    return formattedInt;
  };

  const isResolved = verification.status === "approved" || verification.status === "rejected";
  const isPdf = isPdfFile(verification.proof_url);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-slate-900/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-hidden animate-in fade-in duration-300">
        <div className="bg-white rounded-[2.5rem] w-full max-w-2xl max-h-[85vh] p-6 sm:p-8 shadow-2xl border border-slate-100 flex flex-col relative animate-in zoom-in-95 duration-200">
          
          {/* Header - Fixed top */}
          <div className="flex justify-between items-center pb-4 border-b border-slate-100 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center border border-emerald-100">
                <ShieldCheck size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-black text-slate-800">
                  Payment Verification
                </h3>
                <p className="text-slate-400 font-bold text-xs">
                  Inspect proof of payment receipt and verify payment.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-10 h-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all"
            >
              <X size={20} />
            </button>
          </div>

          {/* Scrollable Form Body */}
          <form onSubmit={handleApprove} className="flex-1 overflow-y-auto my-4 pr-1 space-y-6">
            {/* Status & ID Banner */}
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/70 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                  Invoice Code
                </span>
                <span className="font-bold text-slate-800 text-xs font-mono">
                  {verification.invoice_code || verification.invoice?.invoice_code || verification.invoice_id}
                </span>
              </div>

              <div>
                {verification.status === "approved" ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black bg-emerald-100 text-emerald-700">
                    <CheckCircle2 size={14} /> Approved
                  </span>
                ) : verification.status === "rejected" ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black bg-rose-100 text-rose-700">
                    <X size={14} /> Rejected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black bg-amber-100 text-amber-700">
                    Pending Verification
                  </span>
                )}
              </div>
            </div>

            {/* Student Details Card */}
            {(verification.student || verification.invoice?.student) && (
              <div className="bg-gradient-to-r from-slate-50 to-emerald-50/40 p-4 rounded-2xl border border-slate-200/80 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3.5 min-w-0">
                  <div className="relative w-14 h-14 shrink-0">
                    {verification.student?.profile_picture ? (
                      <img
                        src={verification.student.profile_picture}
                        alt="Student Profile"
                        className="w-14 h-14 rounded-2xl object-cover border-2 border-white shadow-md"
                        onError={(e) => {
                          (e.currentTarget as HTMLElement).style.display = "none";
                          const fallback = e.currentTarget.parentElement?.querySelector(".modal-avatar-fallback");
                          if (fallback) (fallback as HTMLElement).style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={`modal-avatar-fallback w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-800 font-black text-lg items-center justify-center border-2 border-white shadow-md ${
                        verification.student?.profile_picture ? "hidden" : "flex"
                      }`}
                    >
                      {verification.student?.firstname?.[0]?.toUpperCase() ||
                        verification.student?.surname?.[0]?.toUpperCase() ||
                        verification.invoice?.student?.name?.[0]?.toUpperCase() || (
                          <User size={22} />
                        )}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      Student Details
                    </span>
                    <h4 className="font-black text-slate-900 text-base truncate">
                      {verification.student
                        ? [
                            verification.student.firstname,
                            verification.student.othername,
                            verification.student.surname,
                          ]
                            .filter(Boolean)
                            .join(" ")
                        : verification.invoice?.student?.name}
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-slate-500 font-medium">
                      {verification.student?.email && (
                        <span>{verification.student.email}</span>
                      )}
                      {verification.student?.gender && (
                        <span className="inline-block px-2 py-0.5 bg-slate-200/70 text-slate-700 rounded-md font-bold text-[10px] uppercase">
                          {verification.student.gender}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {verification.school_name && (
                  <div className="text-right hidden sm:block shrink-0">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      School
                    </span>
                    <span className="text-xs font-bold text-slate-700 max-w-[180px] block truncate">
                      {verification.school_name}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Verification Metadata Details Strip (Excluding school_signature) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-200/70 text-xs">
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">
                  Amount Paid
                </span>
                <span className="font-black text-emerald-600 text-sm">
                  ₦{Number(verification.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </span>
              </div>
              {verification.status === "approved" && (
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">
                    Verified Amount
                  </span>
                  <span className="font-black text-slate-800 text-sm">
                    ₦{Number(verification.verified_amount || verification.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">
                  Submitted Date
                </span>
                <span className="font-bold text-slate-700">
                  {verification.created_at || "—"}
                </span>
              </div>
              {verification.school_name && (
                <div className="sm:hidden col-span-2">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">
                    School
                  </span>
                  <span className="font-bold text-slate-700">
                    {verification.school_name}
                  </span>
                </div>
              )}
              {verification.approved_at && (
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">
                    Approved At
                  </span>
                  <span className="font-bold text-slate-700">
                    {new Date(verification.approved_at).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {/* Rejection Details */}
            {verification.status === "rejected" && (
              <div className="bg-rose-50 p-4 rounded-2xl border border-rose-100/70 flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center border border-rose-200 shrink-0">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <span className="text-[10px] font-black text-rose-500 uppercase tracking-widest block mb-0.5">
                    Rejection Reason
                  </span>
                  <p className="font-bold text-rose-900 text-sm">
                    {verification.rejection_reason || "No reason provided."}
                  </p>
                  {verification.rejected_at && (
                    <p className="text-[11px] font-bold text-rose-400 mt-1">
                      Rejected on {new Date(verification.rejected_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Proof Image / Document Preview */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">
                  Proof of Payment Receipt
                </label>
                {verification.proof_url && (
                  <a
                    href={verification.proof_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <ExternalLink size={13} /> Open Full Receipt
                  </a>
                )}
              </div>

              <div
                onClick={() => verification.proof_url && setIsPreviewOpen(true)}
                className={`bg-slate-900 rounded-3xl p-4 min-h-[220px] max-h-[300px] flex items-center justify-center overflow-hidden relative group border border-slate-800 ${
                  verification.proof_url ? "cursor-pointer" : ""
                }`}
              >
                {verification.proof_url ? (
                  isPdf ? (
                    <div className="text-center text-slate-200 space-y-3 p-4 flex flex-col items-center justify-center">
                      <div className="w-16 h-16 rounded-2xl bg-red-500/20 border border-red-500/30 text-red-400 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <FileText size={36} />
                      </div>
                      <div>
                        <p className="text-sm font-black text-white">PDF Receipt Document</p>
                        <p className="text-xs font-semibold text-slate-400 mt-0.5">
                          Click to view interactive PDF preview
                        </p>
                      </div>
                      <div className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs transition-all shadow-md mt-2">
                        <Eye size={14} /> Preview PDF Document
                      </div>
                    </div>
                  ) : (
                    <>
                      <img
                        src={verification.proof_url}
                        alt="Proof of Payment"
                        className="max-h-[260px] w-auto object-contain rounded-2xl transition-transform duration-300 group-hover:scale-105"
                        onError={() => setImageLoadError(true)}
                      />
                      <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white font-bold text-xs backdrop-blur-[2px]">
                        <ZoomIn size={20} /> Click to View Full Image
                      </div>
                    </>
                  )
                ) : (
                  <div className="text-center text-slate-400 space-y-2">
                    <ImageIcon size={36} className="mx-auto text-slate-600" />
                    <p className="text-xs font-medium">No proof image uploaded.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Amount and Admin Notes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">
                    Verified Amount (₦)
                  </label>
                  {!isResolved && (
                    <button
                      type="button"
                      onClick={() => setIsAmountEditable(!isAmountEditable)}
                      className="text-[11px] font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-2.5 py-0.5 rounded-lg transition-all"
                    >
                      {isAmountEditable ? (
                        <>
                          <Lock size={12} /> Lock
                        </>
                      ) : (
                        <>
                          <Edit3 size={12} /> Edit / Adjust
                        </>
                      )}
                    </button>
                  )}
                </div>
                <div className="relative flex items-center">
                  <span className="absolute left-3.5 text-slate-400 font-bold text-sm">₦</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={formatNumberWithCommas(verifiedAmount)}
                    onChange={(e) => {
                      const clean = e.target.value.replace(/[,₦\s]/g, "");
                      if (clean === "" || /^[0-9]*\.?[0-9]*$/.test(clean)) {
                        setVerifiedAmount(clean);
                      }
                    }}
                    disabled={isResolved || !isAmountEditable}
                    className={`w-full rounded-2xl border pl-8 pr-4 py-3.5 outline-none font-bold text-sm transition-all ${
                      !isResolved && isAmountEditable
                        ? "bg-white border-blue-400 text-slate-900 ring-2 ring-blue-100"
                        : "bg-[#F8FAFC] border-slate-200 text-slate-700 disabled:opacity-80"
                    }`}
                    required
                  />
                </div>
                {!isResolved && !isAmountEditable && (
                  <p className="text-[10px] font-medium text-slate-400 mt-1 ml-1">
                    Defaults to claimed amount. Click <strong className="text-blue-600 font-bold">Edit / Adjust</strong> if receipt amount differs.
                  </p>
                )}
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                  Admin Notes
                </label>
                <input
                  type="text"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  disabled={isResolved}
                  placeholder="e.g. Payment received with thanks"
                  className="w-full bg-[#F8FAFC] rounded-2xl border border-slate-200 px-4 py-3.5 outline-none font-bold text-slate-800 text-sm focus:border-emerald-500 focus:bg-white transition-all disabled:opacity-60"
                />
              </div>
            </div>
          </form>

          {/* Sticky Action Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 bg-white shrink-0 z-10">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
            >
              Close
            </button>

            {!isResolved && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowRejectModal(true)}
                  disabled={submitting}
                  className="bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <X size={16} />
                  Reject
                </button>
                <button
                  type="submit"
                  onClick={handleApprove}
                  disabled={submitting || !Number(verifiedAmount) || Number(verifiedAmount) <= 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Approving...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      <span>Approve Payment</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Full-Screen Image / PDF Lightbox Preview Modal */}
      {isPreviewOpen && verification.proof_url && (
        <div
          className="fixed inset-0 z-[70] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setIsPreviewOpen(false)}
        >
          <div
            className="relative w-full max-w-5xl max-h-[92vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex justify-between items-center mb-3">
              <div className="flex items-center gap-2 text-white">
                {isPdf ? (
                  <>
                    <FileText size={20} className="text-red-400" />
                    <span className="font-black text-sm">PDF Proof Receipt Document</span>
                  </>
                ) : (
                  <>
                    <ImageIcon size={20} className="text-blue-400" />
                    <span className="font-black text-sm">Receipt Image Preview</span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-3">
                <a
                  href={verification.proof_url}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-blue-600 hover:bg-blue-500 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-md"
                >
                  <ExternalLink size={14} /> Open in New Tab
                </a>
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  className="bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition-all"
                  title="Close Preview"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {isPdf ? (
              <iframe
                src={verification.proof_url}
                title="PDF Receipt Proof"
                className="w-full h-[80vh] rounded-2xl border border-slate-700 bg-white shadow-2xl"
              />
            ) : (
              <img
                src={verification.proof_url}
                alt="Proof of Payment Full View"
                className="max-h-[82vh] max-w-full object-contain rounded-2xl shadow-2xl border border-white/10"
              />
            )}
          </div>
        </div>
      )}

      {/* Reject Payment Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-100 shrink-0">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h4 className="text-lg font-black text-slate-800">
                  Reject Payment
                </h4>
                <p className="text-xs text-slate-400 font-semibold">
                  Please provide a reason for rejection.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200/80 space-y-3">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                Rejection Reason
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g. Blurred receipt, insufficient funds..."
                className="w-full bg-white rounded-xl border border-slate-200 p-3 outline-none font-medium text-sm focus:border-rose-400 min-h-[100px] resize-none"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                disabled={submitting}
                className="flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-wider text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={processRejection}
                disabled={submitting || !rejectionReason.trim()}
                className="flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  "Rejecting..."
                ) : (
                  <>
                    <X size={16} /> Confirm Reject
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

